import { apiClient } from './api';

/**
 * REV-478: client for org integration connections (external issue trackers)
 * and finding export. The access token is write-only — it is sent on create
 * and never returned by the API, so no token field appears on the read shape.
 */

export type IntegrationProvider = 'github';

/** GitHub connection config. The API host is fixed server-side (SSRF guard). */
export interface GithubConfig {
  owner: string;
  repo: string;
}

/** Redacted connection as returned by the API — never carries a token. */
export interface IntegrationConnection {
  id: string;
  provider: IntegrationProvider;
  config: GithubConfig | null;
  connected: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateGithubConnectionInput {
  // REV-514: a connection is AUTHENTICATION. The destination lives on the
  // context (see IntegrationTarget), so a repo is optional here.
  owner?: string;
  repo?: string;
  token: string;
}

/**
 * REV-514: where a tracked place's findings are exported to. The org-level
 * connection says who we are; this says where this context's issues go.
 */
export interface IntegrationTarget {
  id: string;
  context_id: string;
  connection_id: string;
  provider: IntegrationProvider;
  config: GithubConfig | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** REV-492: a target plus the tracked place it belongs to. */
export interface ConnectionTarget extends IntegrationTarget {
  context: { id: string; name: string; slug?: string | null } | null;
}

export interface SetIntegrationTargetInput {
  provider: IntegrationProvider;
  connectionId: string;
  config: GithubConfig;
}

/** REV-697: a repository a connection can file issues in. */
export interface ConnectionRepository {
  owner: string;
  name: string;
  full_name: string;
  private: boolean;
}

export interface ConnectionRepositories {
  repositories: ConnectionRepository[];
  /** More exist than the API read. */
  truncated: boolean;
}

export interface ExportResult {
  external_issue_url: string;
  external_issue_key: string | null;
}

export const integrationService = {
  async list(orgId: string): Promise<IntegrationConnection[]> {
    const res = await apiClient.get<{ success: boolean; data: IntegrationConnection[] }>(
      `/v1/organizations/${orgId}/integrations`
    );
    return res.data;
  },

  async createGithub(
    orgId: string,
    input: CreateGithubConnectionInput
  ): Promise<IntegrationConnection> {
    const res = await apiClient.post<{ success: boolean; data: IntegrationConnection }>(
      `/v1/organizations/${orgId}/integrations`,
      {
        provider: 'github',
        // Omit config entirely for an auth-only connection — sending empty
        // strings would read as a half-specified repo, which the API rejects.
        ...(input.owner && input.repo ? { config: { owner: input.owner, repo: input.repo } } : {}),
        token: input.token,
      }
    );
    return res.data;
  },

  /**
   * REV-492: which tracked places route through a connection — the provider
   * catalog's drill-in, and the answer to "what breaks if I disconnect this?".
   */
  async listConnectionTargets(orgId: string, connectionId: string): Promise<ConnectionTarget[]> {
    const res = await apiClient.get<{ success: boolean; data: ConnectionTarget[] }>(
      `/v1/organizations/${orgId}/integrations/${connectionId}/targets`
    );
    return res.data;
  },

  /**
   * REV-697: what a connection can reach. The API asks GitHub with the stored
   * token, so this is also the test of whether the connection still works.
   */
  async listRepositories(orgId: string, connectionId: string): Promise<ConnectionRepositories> {
    const res = await apiClient.get<{ success: boolean; data: ConnectionRepositories }>(
      `/v1/organizations/${orgId}/integrations/${connectionId}/repositories`
    );
    return res.data;
  },

  async listTargets(orgId: string, contextId: string): Promise<IntegrationTarget[]> {
    const res = await apiClient.get<{ success: boolean; data: IntegrationTarget[] }>(
      `/v1/organizations/${orgId}/contexts/${contextId}/integration-targets`
    );
    return res.data;
  },

  async setTarget(
    orgId: string,
    contextId: string,
    input: SetIntegrationTargetInput
  ): Promise<IntegrationTarget> {
    const res = await apiClient.put<{ success: boolean; data: IntegrationTarget }>(
      `/v1/organizations/${orgId}/contexts/${contextId}/integration-targets`,
      input
    );
    return res.data;
  },

  async removeTarget(
    orgId: string,
    contextId: string,
    provider: IntegrationProvider
  ): Promise<void> {
    await apiClient.delete(
      `/v1/organizations/${orgId}/contexts/${contextId}/integration-targets/${provider}`
    );
  },

  async remove(orgId: string, connectionId: string): Promise<void> {
    await apiClient.delete(`/v1/organizations/${orgId}/integrations/${connectionId}`);
  },

  async exportFinding(
    orgId: string,
    findingInstanceId: string,
    input: { connectionId?: string } = {}
  ): Promise<ExportResult> {
    const res = await apiClient.post<{ success: boolean; data: ExportResult }>(
      `/v1/organizations/${orgId}/finding-instances/${findingInstanceId}/export`,
      input.connectionId ? { connectionId: input.connectionId } : {}
    );
    return res.data;
  },
};
